import {
  BooleanValueType,
  SplitAssignmentWithNullableEntries,
  EnumValueType,
  EventTypeMap,
  Expression,
  FeatureMap,
  FloatValueType,
  InitData,
  IntValueType,
  SplitMap,
  StringValueType,
  Value,
  Schema,
  SplitAssignment,
} from "@hypertune/sdk/src/shared/types";
import { z } from "zod";
import { ObjectValue } from "@hypertune/sdk";
import { graphqlScalarTypeNames } from "./constants";

export type GraphQLScalarTypeName = (typeof graphqlScalarTypeNames)[number];

export type DbJoinableBusiness = {
  id: number;
  name: string;
  member_count: number;
};

// Analytics

export type DbEvent = {
  unique_id: string;
  event_object_type_name: string | null;
  event_payload: ObjectValue | null;
  created_at: string;
  commit_id: number;
  project_id: number; // Redundant column for fast queries

  // @deprecated - use event_object_type_name instead
  event_type_id: string | null;
  // @deprecated - use payload instead
  unit_id: string | null;
  // @deprecated - use payload instead
  feature_values: DbFeatureValues | null; // JSONB
};

/** TODO: Merge DbExposure into DbEvent, where split_id and assignment are just
 * always extra fields in event_payload, and there is no uniqueness on
 * <split_id, unit_id> for debugging purposes.
 */
// Unique on <split_id, unit_id>
export type DbExposure = {
  id: number;
  split_id: string;
  unit_id: string;
  event_object_type_name: string | null;
  event_payload: ObjectValue | null;
  assignment: SplitAssignment; // JSONB
  created_at: string;
  commit_id: number;
  project_id: number; // Redundant column for fast queries

  // @deprecated - use payload instead
  feature_values: DbFeatureValues | null; // JSONB
};

export type DbFeatureValues = { [featureId: string]: Value };

export type DbTableData = { rows: (DbEvent | DbExposure)[]; count: number };

export type DbFunnelData = DbFunnelStepData[];

type DbFunnelStepData = DbFunnelSegmentData[];

export type DbFunnelSegmentData = {
  /** TODO: Rename to breakdown_path_values_array */
  breakdown_path_values_list: DbFeaturePathValues[];
  /** TODO: Rename to assignment_array */
  assignment_list: SplitAssignmentWithNullableEntries[];
  unique_count: number;
  total_count: number;
  aggregations?: number[];
};

export type DbFeaturePathValues = {
  [ssFeaturePath: string]: Value;
};

export type AnalyticsViewConfig =
  | AnalyticsViewConfigFunnel
  | AnalyticsViewConfigTable;

export const analyticsViewConfigFunnelType = "funnel" as const;
export const analyticsViewConfigTableType = "table" as const;

export const analyticsViewConfigTypes = [
  analyticsViewConfigFunnelType,
  analyticsViewConfigTableType,
] as const;

export type AnalyticsViewConfigType = (typeof analyticsViewConfigTypes)[number];

export type AnalyticsViewConfigFunnel = {
  type: typeof analyticsViewConfigFunnelType;
  steps: FunnelStep[];
};

export type AnalyticsViewConfigTable = {
  type: typeof analyticsViewConfigTableType;
  /**
   * We use 'steps' here similar to a funnel as it still makes sense. If there
   * are 0 steps, we treat it as querying all events and exposures in the
   * project. In this case, we should have an additional top-level 'filter' and
   * 'derivedFields' fields which can target fields in the project's Context
   * which should be common across all events and exposures. This could be used,
   * e.g. to query all events and exposures for a specific user ID or
   * environment.
   *
   * Otherwise, each step is treated as querying for events of a specific event
   * type, or exposures of a specific split. In both these cases, we can again
   * attach filters and derived fields, which target fields in the specific
   * event payload type of the event type or split (as well as the Context
   * still, assuming that's always included in all specific event payloads).
   *
   * When there are multiple steps, we return the union of all of them.
   *
   * Finally, we could add top-level fields for 'aggregations' and 'breakdowns'
   * which could apply across all queried data, with default values for columns
   * that aren't present in all the returned rows. So aggregations and
   * breakdowns don't make sense for individual steps.
   *
   * For now, the frontend and backend just assumes there's exactly one step.
   */
  steps: FunnelStep[];
};

export type FunnelStep = FunnelEventStep | FunnelExposureStep;

/** TODO: Refactor to AnalyticsEventStep */
export const funnelEventStepType = "FunnelEventStep" as const;
/** TODO: Refactor to AnalyticsExposureStep */
export const funnelExposureStepType = "FunnelExposureStep" as const;

export const funnelStepTypes = [
  funnelEventStepType,
  funnelExposureStepType,
] as const;

export type FunnelStepType = (typeof funnelStepTypes)[number];

export type FunnelEventStep = BaseFunnelStep & {
  type: typeof funnelEventStepType;
  eventObjectTypeName: string;
  unitIdPayloadPath: string[];
};

export type FunnelExposureStep = BaseFunnelStep & {
  type: typeof funnelExposureStepType;
  splitId: string;
  unitId: { type: "unitId" } | { type: "payloadPath"; payloadPath: string[] };
  dimensionIds: { [dimensionId: string]: true };
};

type BaseFunnelStep = {
  filter?: ExpressionFilter;
  derivedFields?: ExpressionField[];
  breakdowns?: AnalyticsField[];
  aggregations?: AnalyticsAggregation[];
};

export type ExpressionFilter = {
  type: "ExpressionFilter";
  description?: string;
  // Expression with a Boolean value type that can be converted to SQL
  expression: Expression;
};

export type ExpressionField = {
  type: "ExpressionField";
  name: string;
  valueType:
    | BooleanValueType
    | StringValueType
    | EnumValueType
    | IntValueType
    | FloatValueType;
  expression: Expression;
};

export type AnalyticsField =
  | { type: "payloadPath"; payloadPath: string[] }
  | { type: "derivedField"; fieldName: string };

export type AnalyticsAggregation = {
  type: AnalyticsAggregationType;
  name: string;
  data: AnalyticsField;
};

export const analyticsAggregationTypes = [
  "sum",
  "average",
  "minimum",
  "maximum",
] as const;

export type AnalyticsAggregationType =
  (typeof analyticsAggregationTypes)[number];

export type DbArmReward = {
  arm_id: string;
  exposure_count: number;
  reward_sum: number;
};

export type DbArmRewardMap = { [armId: string]: DbArmReward };

// Billing

export type BillingData = {
  name?: string;
  email?: string;
  address?: {
    city?: string;
    country?: string;
    line1?: string;
    line2?: string;
    postal_code?: string;
    state?: string;
  };
  paymentMethod?: PaymentMethod;
  subscriptionEndsAt?: string;
  nextInvoiceTotal?: number;
};

export type PaymentMethod = {
  id: string;
  type: string;
  cardType?: string;
  lastFourDigits?: string;
};

export const sdkVersions = ["1.0.0", "2.0.0", "2.7.0"] as const;

export type SdkVersion = (typeof sdkVersions)[number];

export type DbVercelConnection = {
  id: string;
  projectId: number | null;
  vercelOwnerId: string | null;
  vercelEdgeConfigStoreId: string | null;
  minSdkVersion?: SdkVersion;
  syncAllBranches?: boolean;
};

export type VercelEdgeConfigStore = {
  id?: string;
  createdAt?: number;
  ownerId?: string;
  slug?: string;
  updatedAt?: number;
  digest?: string;
  sizeInBytes: number;
  itemCount: number;
};

export type VercelEdgeConfigStoreWithTokens = VercelEdgeConfigStore & {
  tokens: VercelEdgeConfigStoreToken[];
};

export type VercelEdgeConfigStoreToken = {
  // createdAt: number;
  // edgeConfigId: string;
  id: string;
  // label: string;
  token: string;
};

export type AnalyticsSchema = {
  splits: SplitMap;
  eventTypes: EventTypeMap;
  features: FeatureMap;
};

export type Implementation = AnalyticsSchema & {
  expression: Expression;
};

export type ProjectMetadata = {
  generatedClient?: boolean;
  evaluatedFlags?: boolean;
};

const projectTokenScopes = z.enum([
  "all",
  "query",
  "logs",
  "codegen",
  "flagDefinitions",
]);

export const projectTokenScopesArray = z.array(projectTokenScopes);

export type ProjectTokenScope = z.infer<typeof projectTokenScopes>;

export const projectTokenMapSchema = z.record(
  z.object({ scopes: z.array(projectTokenScopes) })
);

export type ProjectTokenMap = z.infer<typeof projectTokenMapSchema>;

export type ProjectTokenMetadata = {
  /** TODO: Change type to { [scope: ProjectTokenScope]: true } */
  scopes: ProjectTokenScope[];
  name: string;
  createdAt: string;
};

/** TODO: Rename to ProjectTokenMap */
export type ProjectTokenMapWithMeta = {
  [token: string]: ProjectTokenMetadata;
};

export const updateCommitSyncBodySchema = z.object({
  commitId: z.number(),
  type: z.enum(["edge", "vercel", "webhook"]),
  targetId: z.string(),
  status: z.enum(["success", "error"]),
  message: z.nullable(z.string()),
});

export type UpdateCommitSyncBody = z.infer<typeof updateCommitSyncBodySchema>;

const edgeCommitMetadataSchema = z.object({
  allowToolbarGeneration: z.boolean(),
  blockAllRequests: z.optional(z.boolean()),
});

export type EdgeCommitMetadata = z.infer<typeof edgeCommitMetadataSchema>;

export const updateActiveCommitRequestBody = z.object({
  tokenMap: projectTokenMapSchema,
  branch: z.string(),
  businessId: z.number(),
  projectId: z.number(),
  commitId: z.number(),
  hash: z.string(),
  schemaCode: z.string(),
  expressionJson: z.string(),
  splitsJson: z.string(),
  eventTypesJson: z.string(),
  featuresJson: z.string(),
  configJson: z.string(),
  metadata: z.optional(z.nullable(edgeCommitMetadataSchema)),
});

export type UpdateActiveCommitBody = z.infer<
  typeof updateActiveCommitRequestBody
>;

export type BlockedResponse = {
  message: string;
};

// TODO: delete legacy types below when everyone is using sdkVersion >= 2.6.0

export type LegacyHashDataV2 = {
  hash: string;
};

// TODO: delete legacy types below when everyone is using sdkVersion >= 2.0.0

export type LegacyHashData = LegacyHashDataV2 & {
  hash: string;
  commitHash: string;
};

export type LegacyInitData = InitData & {
  commitHash: string;
  eventTypes: EventTypeMap;
  initLogId: number;
  sdkConfig: {
    hashPollInterval: number;
    flushLogsInterval: number;
    maxLogsPerFlush: number;
  };
};

// Codegen types

export type BaseContext = {
  frontendBaseUrl: string;
  rootObjectTypeName: string;
  projectId: number;
  schema: Schema;
  includeDeprecated: boolean;
};

// Vercel flag definition types

export type VercelFlagCode = Record<string, string>;

export type VercelFlagDefinitions = Record<string, VercelFlagDefinition>;

export type VercelFlagDefinition = {
  options?: VercelFlagOption[];
  /**
   * The URL where the feature flag can be managed.
   */
  origin?: string;
  description?: string;
};

export type VercelFlagOption = {
  value: Value;
  label?: string;
};
